import {areas, DataShape} from "../App";
import React from "react";
import {ResponsiveLine} from "@nivo/line";
import {area, curveMonotoneX} from "d3-shape";
import {Defs} from "@nivo/core";
import RtDescription from "./RtDescription";

const ProtectionLayer = ({ series, xScale, yScale }: any) => {
    const areaGenerator = area()
        .x((d: [any, any]) => {
            return xScale(d['data' as any].x)
        })
        .y0((d: [any, any]) => {
            const data = d['data' as any];
            return yScale(data.y);
        })
        .y1((d: [any, any]) => {
            const data = d['data' as any];
            return yScale(data.range);
        })
        .curve(curveMonotoneX)

    return (
        <>
            <Defs
                defs={[
                    {
                        id: 'pattern',
                        type: 'patternLines',
                        background: 'transparent',
                        color: '#fc9926',
                        lineWidth: 1,
                        spacing:5,
                        rotation: -45,
                    },
                ]}
            />
            <path
                d={areaGenerator(series[0].data) || undefined}
                fill="url(#pattern)"
                fillOpacity={0.2}
                stroke="#fc9926"
                strokeWidth={0.5}
            />
        </>
    )
}

export interface RtInfection {
    id: string;
    data: {
        x: any,
        y: any,
        range: number,
        rtHigh: number | null
    }[]
}

const Rt = ({data, message}: { data: DataShape, message: string }) => {
    /**
     * rInit: What the Rt would be if not for the vaccination rate, at some vaccination efficacy.
     *
     * rInit(rt, vacRate, eff) = rt / (1 - (vacRate * eff))
     */
    const rInit = (rt: number, vacRate: number, eff: number): number => {
        return rt / (1 - (vacRate * eff))
    }

    let recentVaccinationsCompletedRatio: number;
    const rtInfection: RtInfection[] = [{
        id: areas[message],
        data: data.metricsTimeseries.filter(el => el.infectionRate !== null && el.infectionRate !== undefined).map(el => {
            const rate: number = el.infectionRate as number;
            if (el.vaccinationsCompletedRatio) { recentVaccinationsCompletedRatio = el.vaccinationsCompletedRatio }
            const range = recentVaccinationsCompletedRatio ? rInit(rate, recentVaccinationsCompletedRatio as number, 0.83) : rate;
            const rtHigh = recentVaccinationsCompletedRatio ? rInit(rate, recentVaccinationsCompletedRatio as number, 0.83) : null;
            return {
                x: el.date,
                y: el.infectionRate,
                range: Math.max(range, rate),
                rtHigh
            }
        })
    }];

    const rtMax = rtInfection[0].data.reduce<number>((acc, val) => { return (val.range > acc) ? val.range : acc }, 0);

    return (
        <>
            <div style={{width: 700, height: 300}}>
                <ResponsiveLine
                    markers={[
                        {
                            axis: 'y',
                            value: 1,
                            lineStyle: {stroke: '#b0413e', strokeWidth: 1},
                            // legend: 'y marker at 0',
                            legendPosition: 'bottom-left',
                        },
                    ]}
                    data={rtInfection}
                    margin={{top: 10, right: 140, bottom: 50, left: 60}}
                    xScale={{
                        format: "%Y-%m-%d",
                        type: "time",
                        useUTC: false,
                    }}
                    xFormat="time:%Y-%m-%d"
                    yScale={{type: 'linear', min: 'auto', max: rtMax, stacked: false, reverse: false}}
                    // yFormat=" >-.2f"
                    curve="cardinal"
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        // tickValues: "every 3 month",
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        format: "%Y-%m",
                        legend: "Date",
                        legendOffset: 36,
                        legendPosition: "middle"
                    }}
                    axisLeft={{
                        // orient: 'left',
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Rt',
                        legendOffset: -45,
                        legendPosition: 'middle'
                    }}
                    enablePoints={false}
                    pointSize={10}
                    pointColor={{theme: 'background'}}
                    pointBorderWidth={2}
                    pointBorderColor={{from: 'serieColor'}}
                    pointLabelYOffset={-12}
                    layers={[
                        'grid',
                        'markers',
                        'areas',
                        ProtectionLayer,
                        'lines',
                        'slices',
                        'axes',
                        'points',
                        'legends',
                    ]}
                    isInteractive={true}
                    // useMesh={true}
                    enableSlices={"x"}
                    sliceTooltip={({slice}) => {
                        return (
                            <div
                                style={{
                                    background: 'white',
                                    padding: '9px 12px',
                                    border: '1px solid #ccc',
                                }}
                            >
                                <div>{slice.points[0].data.xFormatted}</div>
                                {slice.points.map(point => (
                                    <div
                                        key={point.id}
                                        style={{
                                            color: point.serieColor,
                                            padding: '3px 0',
                                        }}
                                    >
                                        <strong>{point.serieId}</strong> {point.data.yFormatted}
                                    </div>
                                ))}
                            </div>
                        )
                    }}
                    legends={[
                        {
                            anchor: 'bottom-right',
                            direction: 'column',
                            justify: false,
                            translateX: 100,
                            translateY: 0,
                            itemsSpacing: 0,
                            itemDirection: 'left-to-right',
                            itemWidth: 80,
                            itemHeight: 20,
                            itemOpacity: 0.75,
                            symbolSize: 12,
                            symbolShape: 'circle',
                            symbolBorderColor: 'rgba(0, 0, 0, .5)',
                            effects: [
                                {
                                    on: 'hover',
                                    style: {
                                        itemBackground: 'rgba(0, 0, 0, .03)',
                                        itemOpacity: 1
                                    }
                                }
                            ]
                        }
                    ]}
                />
            </div>
            <RtDescription data={data} message={message} rtInfection={rtInfection}/>
        </>
    );
}

export default Rt;